import { useState, useEffect } from 'react'
import { useLazyQuery } from '@apollo/client'

import { truncate } from '@/lib/utils'
import CloseIcon from '@/assets/CloseIcon'
import { Button } from '@/components/ui/button'
import LocalizeIcon from '@/assets/LocalizeIcon'
import { getEntitiesData as getEntitiesDataQuery } from '@/lib/graphQueries'
import { NodeObjectType } from '../graph/types'

import { InfoCard } from './info-card'

import AutoMoveTopRightPanel from './auto-move-top-right-panel'

interface EntityDetailPanelProps {
  selectedNode: NodeObjectType | null
  handleLocalizeNode: (nodeData: NodeObjectType) => void
  setSelectedNodeID: (id: string | null) => void
}

export default function EntityDetailPanel({
  selectedNode,
  handleLocalizeNode,
  setSelectedNodeID,
}: EntityDetailPanelProps) {
  const [useLongTitle, setUseLongTitle] = useState(false)
  const [nodeData, setNodeData] = useState<NodeObjectType | null>(null)

  const [getEntitiesData, { data: entitiesData }] = useLazyQuery(getEntitiesDataQuery)

  const getSecondaryEntityKeys = (data: any): string[] => {
    const uuids: string[] = []

    const extractUUIDs = (value: any) => {
      if (typeof value === 'string' && /^[0-9a-fA-F-]{36}$/.test(value)) {
        uuids.push(value)
      } else if (Array.isArray(value)) {
        value.forEach(extractUUIDs)
      } else if (value && typeof value === 'object') {
        Object.values(value).forEach(extractUUIDs)
      }
    }

    extractUUIDs(data)
    return uuids
  }

  const updateNodeDataWithSecondaryEntitiesData = (queryResult: any) => {
    // Create a map of UUIDs to their corresponding details
    const uuidMap = queryResult.getEntitiesData.nodes.reduce(
      (
        map: Record<string, any>,
        node: { id: string; data: any; feeds: Array<string>; entityType: string }
      ) => {
        map[node.id] = {
          data: node.data,
          feeds: node.feeds,
          entityType: node.entityType,
        }
        return map
      },
      {}
    )

    // Recursive function to replace UUIDs
    const replace = (value: any): any => {
      if (typeof value === 'string' && uuidMap[value]) {
        return uuidMap[value] // Replace UUID with its corresponding dictionary
      } else if (Array.isArray(value)) {
        return value.map(replace) // Process arrays recursively
      } else if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, replace(val)])) // Process objects recursively
      }
      return value
    }

    if (nodeData) {
      const updatedData = replace(nodeData.data)
      setNodeData({
        ...selectedNode,
        data: updatedData,
      })
    }
  }

  useEffect(() => {
    if (selectedNode) {
      const entityKeys = getSecondaryEntityKeys(selectedNode.data || {})
      if (entityKeys.length > 0) {
        getEntitiesData({ variables: { entities: entityKeys, retrieveSecondaryEntities: false } })
      }
    }
    setNodeData(selectedNode)
  }, [selectedNode])

  useEffect(() => {
    if (entitiesData) {
      updateNodeDataWithSecondaryEntitiesData(entitiesData)
    }
  }, [entitiesData])

  if (!selectedNode || !nodeData) {
    return null
  }

  return (
    <div className="absolute right-0 m-8 z-40 w-4/12 overflow-x-auto overflow-y-hidden border rounded-lg">
      <AutoMoveTopRightPanel
        shouldMove={false}
        moveDistance={360}
        isAccordian
        showself={nodeData}
        setUseLongTitle={setUseLongTitle}
        panelAction={
          <Button
            variant="outline"
            className="flex items-center justify-center"
            onClick={() => handleLocalizeNode(nodeData)}
          >
            <LocalizeIcon className="w-4 h-4" />
          </Button>
        }
        panelTitle={
          nodeData && (
            <div className="flex items-center gap-2 h-16">
              <button
                className="rounded-full w-4 h-4 group flex items-center justify-center cursor-pointer"
                style={{ backgroundColor: nodeData?.color }}
                onClick={() => setSelectedNodeID(null)}
                type="button"
              >
                <CloseIcon className="group-hover:opacity-100 transform ease-in-out duration-200 opacity-0 text-white w-[10px] h-[10px]" />
              </button>
              <div className="font-bold">{truncate(nodeData?.label, useLongTitle ? 40 : 22)}</div>
            </div>
          )
        }
        accordionContent={
          <div className="mt-[-20px]">
            <InfoCard node={nodeData} />
          </div>
        }
      />
    </div>
  )
}
